import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  X,
  FileSignature,
  Loader2,
  CheckCircle,
  Box,
  Layers2,
  Download,
  MessageSquare,
  ChevronRight,
  ExternalLink,
  ImageIcon,
} from 'lucide-react';
import {
  CONCEPT_MODEL_APPROVAL_DISCLAIMER,
  recordPortalDeliverableApproval,
  isDeliverableApproved,
  isInteractiveModelApproved,
  isPreviewImageApproved,
} from '../../utils/portalDeliverableApprovalService';
import { portalHostedAssetHref, portalHtmlPreviewHref } from '../../utils/portalAssetUrl';
import {
  initialsSignatureDataUrl,
  isValidApprovalInitials,
} from '../../utils/initialsSignature';
import {
  logPortalActivity,
  usePortal3dModelEngagementSession,
  usePortalPdfEngagementSession,
} from '../../utils/portalActivityLogger';
import type { PortalDeliverableApproval, PortalFileRelease } from '../../types';
import './PortalDesignApprovalWalkthrough.css';

export interface PortalDesignApprovalWalkthroughProps {
  open: boolean;
  onClose: () => void;
  portalId: string;
  dealId: string;
  modelUrl?: string;
  modelImageUrl?: string;
  modelRelease?: PortalFileRelease | null;
  modelBundleId?: string;
  modelApproval?: PortalDeliverableApproval;
  conceptUrl?: string;
  conceptRelease?: PortalFileRelease | null;
  conceptBundleId?: string;
  conceptApproval?: PortalDeliverableApproval;
  isDesktopPortal?: boolean;
  activitySource?: 'client' | 'employee' | 'stakeholder';
  onOpenConceptFeedback?: () => void;
  onModelApproved?: () => void;
  onConceptApproved?: () => void;
  canSignProposal?: boolean;
  onSignProposal?: () => void;
}

type WalkthroughPhase = 'intro' | 'model' | 'image' | 'concept' | 'done';

const PortalDesignApprovalWalkthrough: React.FC<PortalDesignApprovalWalkthroughProps> = ({
  open,
  onClose,
  portalId,
  dealId,
  modelUrl,
  modelImageUrl,
  modelRelease,
  modelBundleId,
  modelApproval,
  conceptUrl,
  conceptRelease,
  conceptBundleId,
  conceptApproval,
  isDesktopPortal = true,
  activitySource = 'client',
  onOpenConceptFeedback,
  onModelApproved,
  onConceptApproved,
  canSignProposal = false,
  onSignProposal,
}) => {
  const hasModelInteractive = !!modelUrl?.trim();
  const hasModelImage = !!modelImageUrl?.trim() && !hasModelInteractive;
  const hasModel = hasModelInteractive || hasModelImage;
  const hasConcept = !!(conceptUrl || conceptRelease?.releasedUrl);
  const modelApproved = hasModelImage
    ? isPreviewImageApproved(modelApproval, modelImageUrl)
    : isInteractiveModelApproved(modelApproval, modelRelease?.id);
  const conceptApproved = isDeliverableApproved(conceptApproval, conceptRelease?.id);
  const needsModelInteractive = hasModelInteractive && !modelApproved;
  const needsModelImage = hasModelImage && !modelApproved;
  const needsModel = needsModelInteractive || needsModelImage;
  const needsConcept = hasConcept && !conceptApproved;

  const initialPhase = useMemo((): WalkthroughPhase => {
    if (!needsModel && !needsConcept) return 'done';
    if (needsModelImage && !needsModelInteractive && !needsConcept) return 'image';
    if (needsModelInteractive) return 'intro';
    if (needsModelImage) return 'intro';
    return 'concept';
  }, [needsModel, needsModelInteractive, needsModelImage, needsConcept]);

  const [phase, setPhase] = useState<WalkthroughPhase>(initialPhase);
  const [initials, setInitials] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  usePortal3dModelEngagementSession(portalId, dealId, activitySource, open && phase === 'model', {
    releaseId: modelRelease?.id,
    context: 'design_approval_walkthrough',
  });

  usePortalPdfEngagementSession(portalId, dealId, activitySource, open && phase === 'concept', {
    releaseId: conceptRelease?.id,
    documentTitle: conceptRelease?.displayLabel || conceptRelease?.name,
    context: 'design_approval_walkthrough',
  });

  useEffect(() => {
    if (open) {
      setPhase(initialPhase);
      setInitials('');
      setSubmitting(false);
      setError(null);
    }
  }, [open, initialPhase]);

  const resetState = useCallback(() => {
    setPhase(initialPhase);
    setInitials('');
    setSubmitting(false);
    setError(null);
  }, [initialPhase]);

  const handleClose = useCallback(() => {
    resetState();
    onClose();
  }, [onClose, resetState]);

  const buildSignature = useCallback(() => {
    const signedBy = initials.trim();
    return {
      dataUrl: initialsSignatureDataUrl(signedBy),
      signedBy,
    };
  }, [initials]);

  const submitImageOnlyApproval = async () => {
    if (!modelImageUrl || !isValidApprovalInitials(initials)) return;
    const signature = buildSignature();
    setSubmitting(true);
    setError(null);
    try {
      await recordPortalDeliverableApproval({
        portalId,
        dealId,
        deliverableType: 'model',
        signature,
        assetUrl: modelImageUrl.trim(),
      });
      onModelApproved?.();
      void logPortalActivity(
        portalId,
        dealId,
        'action',
        'approved_concept_image_deliverable',
        {
          assetUrl: modelImageUrl.trim(),
          signedBy: signature.signedBy,
          context: 'design_approval_walkthrough',
        },
        activitySource,
      );
      setPhase('done');
    } catch (e) {
      console.error(e);
      setError('Could not record your concept preview approval. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const submitModelOnlyApproval = async () => {
    if (!isValidApprovalInitials(initials)) return;
    const signature = buildSignature();
    setSubmitting(true);
    setError(null);
    try {
      await recordPortalDeliverableApproval({
        portalId,
        dealId,
        deliverableType: 'model',
        signature,
        releaseId: modelRelease?.id,
        bundleId: modelBundleId,
        releaseVersionIndex: modelRelease?.releaseVersionIndex,
      });
      onModelApproved?.();
      void logPortalActivity(
        portalId,
        dealId,
        'action',
        'approved_3d_model_deliverable',
        {
          releaseId: modelRelease?.id,
          bundleId: modelBundleId,
          signedBy: signature.signedBy,
          context: 'design_approval_walkthrough',
        },
        activitySource,
      );
      setPhase('done');
    } catch (e) {
      console.error(e);
      setError('Could not record your 3D model approval. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const submitConceptStepApproval = async () => {
    if (!isValidApprovalInitials(initials)) return;
    const signature = buildSignature();
    setSubmitting(true);
    setError(null);
    try {
      if (needsModelInteractive && !modelApproved) {
        await recordPortalDeliverableApproval({
          portalId,
          dealId,
          deliverableType: 'model',
          signature,
          releaseId: modelRelease?.id,
          bundleId: modelBundleId,
          releaseVersionIndex: modelRelease?.releaseVersionIndex,
        });
        onModelApproved?.();
        void logPortalActivity(
          portalId,
          dealId,
          'action',
          'approved_3d_model_deliverable',
          {
            releaseId: modelRelease?.id,
            bundleId: modelBundleId,
            signedBy: signature.signedBy,
            context: 'design_approval_walkthrough',
          },
          activitySource,
        );
      }
      await recordPortalDeliverableApproval({
        portalId,
        dealId,
        deliverableType: 'concept',
        signature,
        releaseId: conceptRelease?.id,
        bundleId: conceptBundleId,
        releaseVersionIndex: conceptRelease?.releaseVersionIndex,
      });
      onConceptApproved?.();
      void logPortalActivity(
        portalId,
        dealId,
        'action',
        'approved_2d_concept_deliverable',
        {
          releaseId: conceptRelease?.id,
          bundleId: conceptBundleId,
          signedBy: signature.signedBy,
          combinedWithModel: needsModelInteractive,
          context: 'design_approval_walkthrough',
        },
        activitySource,
      );
      setPhase('done');
    } catch (e) {
      console.error(e);
      setError('Could not record your approval. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const submitConceptOnlyApproval = async () => {
    if (!isValidApprovalInitials(initials)) return;
    const signature = buildSignature();
    setSubmitting(true);
    setError(null);
    try {
      await recordPortalDeliverableApproval({
        portalId,
        dealId,
        deliverableType: 'concept',
        signature,
        releaseId: conceptRelease?.id,
        bundleId: conceptBundleId,
        releaseVersionIndex: conceptRelease?.releaseVersionIndex,
      });
      onConceptApproved?.();
      void logPortalActivity(
        portalId,
        dealId,
        'action',
        'approved_2d_concept_deliverable',
        {
          releaseId: conceptRelease?.id,
          bundleId: conceptBundleId,
          signedBy: signature.signedBy,
          context: 'design_approval_walkthrough',
        },
        activitySource,
      );
      setPhase('done');
    } catch (e) {
      console.error(e);
      setError('Could not record your 2D concept approval. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  const modelPreviewUrl = modelUrl
              ? portalHtmlPreviewHref(modelUrl, {
        downloadName: modelRelease?.name || '3D Model.html',
        sizeBytes: modelRelease?.size,
      })
    : '';
  const conceptPreviewUrl = portalHostedAssetHref(
    conceptUrl || conceptRelease?.releasedUrl || '',
  );
  const conceptDownloadUrl = portalHostedAssetHref(
    conceptUrl || conceptRelease?.releasedUrl || '',
    { downloadName: conceptRelease?.name || 'concept-elevations.pdf' },
  );

  const stepCount = (needsModel ? 1 : 0) + (needsConcept ? 1 : 0);
  const currentStep =
    phase === 'model' || phase === 'image'
      ? 1
      : phase === 'concept'
        ? needsModel
          ? 2
          : 1
        : stepCount;

  const isReviewPhase = phase === 'model' || phase === 'image' || phase === 'concept';
  const canApprove = isValidApprovalInitials(initials);
  const showModelAdvanceOnly = phase === 'model' && hasConcept && needsConcept;
  const showModelApproveOnly = phase === 'model' && !showModelAdvanceOnly;
  const showConceptCombinedApprove =
    phase === 'concept' && needsModelInteractive && hasConcept;
  const showConceptOnlyApprove = phase === 'concept' && !needsModel;
  const imagePreviewUrl = modelImageUrl ? portalHostedAssetHref(modelImageUrl) : '';

  const renderInitialsApproval = (approveLabel: string, onApprove: () => void) => (
    <div className="pdaw-sidebar-approval">
      <label className="pdaw-initials-label" htmlFor="pdaw-initials-input">
        Type your initials to approve
      </label>
      <input
        id="pdaw-initials-input"
        type="text"
        className="pdaw-initials-input"
        value={initials}
        onChange={e => setInitials(e.target.value.toUpperCase().slice(0, 8))}
        placeholder="e.g. JD"
        autoComplete="name"
        disabled={submitting}
        maxLength={8}
      />
      <p className="pdaw-initials-hint">
        Your initials act as your electronic signature and are legally binding.
      </p>
      <button
        type="button"
        className="pdaw-primary pdaw-sidebar-primary"
        onClick={() => void onApprove()}
        disabled={!canApprove || submitting}
      >
        {submitting ? <Loader2 size={16} className="pdaw-spin" /> : null}
        {approveLabel}
      </button>
    </div>
  );

  return (
    <div className="pdaw-overlay" role="presentation" onClick={handleClose}>
      <div
        className={`pdaw-shell${isReviewPhase ? ' pdaw-shell--review' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label="Review and approve design deliverables"
        onClick={e => e.stopPropagation()}
      >
        <header className="pdaw-header">
          <div>
            <h2>
              <FileSignature size={18} aria-hidden />
              Review &amp; Approve
            </h2>
            {isReviewPhase && stepCount > 0 && (
              <p className="pdaw-progress">
                Step {currentStep} of {stepCount}
              </p>
            )}
          </div>
          <button type="button" className="pdaw-close" onClick={handleClose} aria-label="Close">
            <X size={18} />
          </button>
        </header>

        {phase === 'intro' && (
          <div className="pdaw-body">
            <p className="pdaw-lead">
              {hasModelInteractive ? (
                <>
                  We&apos;ll walk you through your design deliverables — first your interactive 3D model
                  {hasConcept ? ', then your 2D concept elevations' : ''}. Review each in the viewer,
                  then approve when you&apos;re ready. Approval confirms the model is a reasonable
                  approximation of what Leiper&apos;s Creek Timber Frames will provide from your plans
                  — not final dimensions or joinery.
                </>
              ) : hasModelImage ? (
                <>
                  Review your concept design preview below. When you&apos;re satisfied with the design,
                  enter your initials to approve and unlock proposal signing.
                </>
              ) : (
                <>
                  Review your 2D concept elevations, then approve when you&apos;re ready.
                </>
              )}
            </p>
            <ul className="pdaw-checklist">
              {hasModelInteractive && (
                <li>
                  <Box size={14} aria-hidden />
                  3D Model {modelApproved ? '(approved)' : ''}
                </li>
              )}
              {hasModelImage && (
                <li>
                  <ImageIcon size={14} aria-hidden />
                  Concept Preview {modelApproved ? '(approved)' : ''}
                </li>
              )}
              {hasConcept && (
                <li>
                  <Layers2 size={14} aria-hidden />
                  2D Concept Elevations {conceptApproved ? '(approved)' : ''}
                </li>
              )}
            </ul>
            <footer className="pdaw-footer">
              <button type="button" className="pdaw-secondary" onClick={handleClose}>
                Not now
              </button>
              <button
                type="button"
                className="pdaw-primary"
                onClick={() =>
                  setPhase(
                    needsModelInteractive ? 'model' : needsModelImage ? 'image' : 'concept',
                  )
                }
              >
                Get started
                <ChevronRight size={16} />
              </button>
            </footer>
          </div>
        )}

        {phase === 'model' && modelUrl && (
          <div className="pdaw-review-layout">
            <div className="pdaw-review-stage">
              <iframe
                src={modelPreviewUrl}
                title="3D model review"
                className="pdaw-review-iframe"
                sandbox="allow-scripts allow-same-origin allow-popups allow-pointer-lock allow-fullscreen"
                allow="fullscreen"
              />
              <a
                href={portalHostedAssetHref(modelUrl, {
                  downloadName: modelRelease?.name || '3D Model.html',
                })}
                target="_blank"
                rel="noopener noreferrer"
                className="pdaw-review-fallback-link"
              >
                <ExternalLink size={14} aria-hidden />
                Open in new tab
              </a>
            </div>
            <aside className="pdaw-review-sidebar">
              <div className="pdaw-step-label">
                <Box size={16} aria-hidden />
                3D Model
              </div>
              <p className="pdaw-copy">
                {showModelAdvanceOnly
                  ? 'Explore the model at your own pace. When you are ready, continue to your 2D concept elevations.'
                  : 'Explore the model at your own pace. When you are satisfied, enter your initials below to approve.'}
              </p>
              {showModelApproveOnly ? (
                <p className="pdaw-disclaimer">{CONCEPT_MODEL_APPROVAL_DISCLAIMER}</p>
              ) : null}
              {showModelAdvanceOnly ? (
                <button
                  type="button"
                  className="pdaw-primary pdaw-sidebar-primary"
                  onClick={() => {
                    void logPortalActivity(
                      portalId,
                      dealId,
                      'action',
                      'advanced_to_concept_review',
                      {
                        releaseId: modelRelease?.id,
                        bundleId: modelBundleId,
                        context: 'design_approval_walkthrough',
                      },
                      activitySource,
                    );
                    setPhase('concept');
                  }}
                  disabled={submitting}
                >
                  Continue to 2D drawings
                  <ChevronRight size={16} />
                </button>
              ) : null}
              {showModelApproveOnly
                ? renderInitialsApproval('Approve 3D Model', submitModelOnlyApproval)
                : null}
              {error && (
                <p className="pdaw-error" role="alert">
                  {error}
                </p>
              )}
              <footer className="pdaw-sidebar-footer">
                <button
                  type="button"
                  className="pdaw-secondary"
                  onClick={handleClose}
                  disabled={submitting}
                >
                  Cancel
                </button>
              </footer>
            </aside>
          </div>
        )}

        {phase === 'image' && modelImageUrl && (
          <div className="pdaw-review-layout">
            <div className="pdaw-review-stage pdaw-review-stage--image">
              <img
                src={imagePreviewUrl}
                alt="Concept design preview"
                className="pdaw-review-image"
              />
              <a
                href={imagePreviewUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="pdaw-review-fallback-link"
              >
                <ExternalLink size={14} aria-hidden />
                Open full size
              </a>
            </div>
            <aside className="pdaw-review-sidebar">
              <div className="pdaw-step-label">
                <ImageIcon size={16} aria-hidden />
                Concept Preview
              </div>
              <p className="pdaw-copy">
                Review the concept design image. When you are satisfied, enter your initials below to
                approve and unlock proposal signing.
              </p>
              <p className="pdaw-disclaimer">{CONCEPT_MODEL_APPROVAL_DISCLAIMER}</p>
              {renderInitialsApproval('Approve Concept Preview', submitImageOnlyApproval)}
              {error && (
                <p className="pdaw-error" role="alert">
                  {error}
                </p>
              )}
              <footer className="pdaw-sidebar-footer">
                <button
                  type="button"
                  className="pdaw-secondary"
                  onClick={handleClose}
                  disabled={submitting}
                >
                  Cancel
                </button>
              </footer>
            </aside>
          </div>
        )}

        {phase === 'concept' && (conceptUrl || conceptRelease?.releasedUrl) && (
          <div className="pdaw-review-layout">
            <div className="pdaw-review-stage">
              <iframe
                src={conceptPreviewUrl}
                title="2D concept elevations review"
                className="pdaw-review-iframe"
              />
              <a
                href={conceptDownloadUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="pdaw-review-fallback-link"
                download
              >
                <Download size={14} aria-hidden />
                Download PDF
              </a>
            </div>
            <aside className="pdaw-review-sidebar">
              <div className="pdaw-step-label">
                <Layers2 size={16} aria-hidden />
                2D Concept Elevations
              </div>
              <p className="pdaw-copy">
                Review your concept drawings below. When you are satisfied, enter your initials to
                approve{needsModel ? ' your design deliverables' : ''}.
              </p>
              {showConceptCombinedApprove ? (
                <p className="pdaw-disclaimer">{CONCEPT_MODEL_APPROVAL_DISCLAIMER}</p>
              ) : null}
              <div className="pdaw-action-row pdaw-sidebar-actions">
                <a
                  href={conceptDownloadUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="pdaw-action-link"
                  download
                >
                  <Download size={16} />
                  Download
                </a>
                {conceptRelease && isDesktopPortal && onOpenConceptFeedback && (
                  <button
                    type="button"
                    className="pdaw-action-link pdaw-action-btn"
                    onClick={() => {
                      onOpenConceptFeedback();
                      handleClose();
                    }}
                    aria-label="Comment and draw on concept drawings"
                  >
                    <MessageSquare size={16} aria-hidden />
                    Comment &amp; draw
                  </button>
                )}
              </div>
              {showConceptCombinedApprove
                ? renderInitialsApproval('Approve design', submitConceptStepApproval)
                : null}
              {showConceptOnlyApprove
                ? renderInitialsApproval('Approve 2D Concept', submitConceptOnlyApproval)
                : null}
              {error && (
                <p className="pdaw-error" role="alert">
                  {error}
                </p>
              )}
              <footer className="pdaw-sidebar-footer">
                {needsModelInteractive || needsModelImage ? (
                  <button
                    type="button"
                    className="pdaw-secondary"
                    onClick={() => {
                      setInitials('');
                      setError(null);
                      setPhase(needsModelInteractive ? 'model' : 'image');
                    }}
                    disabled={submitting}
                  >
                    Back
                  </button>
                ) : (
                  <button
                    type="button"
                    className="pdaw-secondary"
                    onClick={handleClose}
                    disabled={submitting}
                  >
                    Cancel
                  </button>
                )}
              </footer>
            </aside>
          </div>
        )}

        {phase === 'done' && !hasModel && !hasConcept && (
          <div className="pdaw-body pdaw-done">
            <Box size={40} aria-hidden />
            <p className="pdaw-done-lead">
              Your concept model has not been released yet. There is nothing to approve, and signing
              stays closed until we publish it for review.
            </p>
            <button type="button" className="pdaw-primary" onClick={handleClose}>
              Close
            </button>
          </div>
        )}

        {phase === 'done' && (hasModel || hasConcept) && (
          <div className="pdaw-body pdaw-done">
            <CheckCircle size={40} aria-hidden />
            <p className="pdaw-done-lead">
              Thank you — your concept approvals have been recorded. Your project team has been
              notified.
            </p>
            <ol className="pdaw-done-steps">
              {hasModelInteractive ? (
                <li className="pdaw-done-step pdaw-done-step--done">
                  <CheckCircle size={16} aria-hidden />
                  <span>
                    <strong>3D model</strong> — approved
                  </span>
                </li>
              ) : null}
              {hasModelImage ? (
                <li className="pdaw-done-step pdaw-done-step--done">
                  <CheckCircle size={16} aria-hidden />
                  <span>
                    <strong>Concept preview</strong> — approved
                  </span>
                </li>
              ) : null}
              {hasConcept ? (
                <li className="pdaw-done-step pdaw-done-step--done">
                  <CheckCircle size={16} aria-hidden />
                  <span>
                    <strong>2D concept drawings</strong> — approved
                  </span>
                </li>
              ) : null}
              <li
                className={`pdaw-done-step ${
                  canSignProposal ? 'pdaw-done-step--next' : 'pdaw-done-step--pending'
                }`}
              >
                <FileSignature size={16} aria-hidden />
                <span>
                  <strong>Sign your proposal</strong> — the last step before Leiper&apos;s Creek
                  Timber Frames takes over.
                </span>
              </li>
            </ol>
            {canSignProposal && onSignProposal ? (
              <>
                <p className="pdaw-done-copy">
                  Click below to open the proposal and sign it there. Once it&apos;s signed, we take
                  it from here.
                </p>
                <button
                  type="button"
                  className="pdaw-primary pdaw-done-sign"
                  onClick={onSignProposal}
                >
                  Sign Proposal
                  <ChevronRight size={18} aria-hidden />
                </button>
              </>
            ) : (
              <button type="button" className="pdaw-primary" onClick={handleClose}>
                Close
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default PortalDesignApprovalWalkthrough;
